<script setup lang="ts">
import { Link } from '@inertiajs/vue3';
import { ArrowRight } from 'lucide-vue-next';
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import AudienceToggle from '@/components/home/AudienceToggle.vue';
import { Button } from '@/components/ui/button';
import { TenantType } from '@/config/constants/enums';
import { HERO_VALUE_PROPS } from '@/config/constants/home';
import { AUTH_ROUTES } from '@/config/constants/routes';

const { t } = useI18n();

const audience = defineModel<TenantType>('audience', { required: true });

const isVisible = ref(false);
const isFlipped = ref(audience.value === TenantType.AGENT);

onMounted(() => {
    setTimeout(() => {
        isVisible.value = true;
    }, 100);
});

watch(audience, (val) => {
    isFlipped.value = val === TenantType.AGENT;
});

const supplierValueProps = computed(() =>
    HERO_VALUE_PROPS[TenantType.SUPPLIER].map(({ key, comingSoon }) => ({
        title: t(`${key}.title`),
        description: t(`${key}.description`),
        comingSoon: comingSoon ?? false,
    })),
);

const agentValueProps = computed(() =>
    HERO_VALUE_PROPS[TenantType.AGENT].map(({ key, comingSoon }) => ({
        title: t(`${key}.title`),
        description: t(`${key}.description`),
        comingSoon: comingSoon ?? false,
    })),
);
</script>

<template>
    <section class="dark:bg-background relative overflow-hidden bg-white">
        <!-- Organic Background Shape -->
        <div class="pointer-events-none absolute inset-0 overflow-hidden">
            <svg
                class="absolute top-0 -right-1/4 h-full w-3/4 text-teal-300/30 dark:text-teal-500/10"
                viewBox="0 0 800 1000"
                fill="none"
                preserveAspectRatio="xMidYMid slice"
            >
                <path d="M400 0C600 200 800 300 800 500C800 700 600 800 400 1000C200 800 0 700 0 500C0 300 200 200 400 0Z" fill="currentColor" />
            </svg>
        </div>

        <!-- Content -->
        <div class="relative container mx-auto px-6 pt-24 lg:px-8 lg:pt-32">
            <!-- Mission Statement -->
            <div
                class="mb-8 text-center transition-all duration-1000 lg:mb-14"
                :class="isVisible ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0'"
            >
                <h1
                    class="font-display mx-auto max-w-7xl text-4xl leading-[1.1] font-normal tracking-tight text-balance text-gray-900 sm:text-5xl lg:text-6xl dark:text-white"
                >
                    {{ t('home.mission.before') }}
                    <span class="text-gray-900 italic dark:text-white">{{ t('home.mission.accent') }}</span>
                    <br />
                    {{ t('home.mission.after') }}
                </h1>

                <!-- Simple spacer -->
                <div class="mt-5 lg:mt-6"></div>
            </div>

            <!-- Audience Switcher -->
            <div
                class="mb-4 flex justify-center transition-all duration-700 lg:mb-6"
                :class="isVisible ? 'translate-y-0 opacity-100' : '-translate-y-4 opacity-0'"
                style="transition-delay: 0.3s"
            >
                <AudienceToggle v-model="audience" />
            </div>

            <!-- Flight path below toggle — dot down, bend right, bend back up, dot -->
            <div
                class="mx-auto mb-10 flex w-40 items-center justify-center transition-all duration-700 lg:mb-14 lg:w-48"
                :class="isVisible ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0'"
                style="transition-delay: 0.45s"
            >
                <svg viewBox="0 0 160 56" fill="none" class="w-full" aria-hidden="true">
                    <!-- Teal dot top-left -->
                    <circle cx="20" cy="6" r="4.5" fill="#0097B2" />
                    <!-- Path: down, rounded bend right, horizontal, rounded bend up, up -->
                    <path
                        d="M20 10.5 L20 36 Q20 44 28 44 L132 44 Q140 44 140 36 L140 10.5"
                        stroke="#0097B2"
                        stroke-width="1.5"
                        stroke-dasharray="4 5"
                        stroke-linecap="round"
                        fill="none"
                    />
                    <!-- Teal dot top-right -->
                    <circle cx="140" cy="6" r="4.5" fill="#0097B2" />
                </svg>
            </div>

            <!-- Flip Scene -->
            <div class="hero-flip-scene transition-all duration-1000" :class="isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'">
                <div class="hero-flip-card" :class="{ 'is-flipped': isFlipped }">
                    <!-- ═══════════════════════════════════════ -->
                    <!-- FRONT FACE: Supplier                    -->
                    <!-- ═══════════════════════════════════════ -->
                    <div class="hero-flip-face hero-flip-face--front" :inert="isFlipped || undefined">
                        <div class="grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
                            <!-- Left Column -->
                            <div>
                                <p
                                    class="mb-6 font-medium tracking-widest text-gray-400 dark:text-gray-500"
                                    style="font-size: 0.7rem; letter-spacing: 0.2em"
                                >
                                    {{ t('home.hero.supplier.eyebrow') }}
                                </p>

                                <h2
                                    class="font-display text-4xl leading-[1.1] font-normal tracking-tight text-gray-900 sm:text-5xl lg:text-6xl xl:text-7xl dark:text-white"
                                >
                                    {{ t('home.hero.supplier.headline_before') }}
                                    <span class="text-gray-900 italic dark:text-white">
                                        {{ t('home.hero.supplier.headline_accent') }}
                                    </span>
                                </h2>

                                <p class="mt-5 max-w-lg text-lg leading-relaxed text-gray-500 lg:text-xl dark:text-gray-400">
                                    {{ t('home.hero.supplier.subheadline') }}
                                </p>

                                <div class="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center">
                                    <Button
                                        as-child
                                        size="lg"
                                        class="group h-14 rounded-none border-2 border-orange-500 bg-orange-500 px-10 text-base font-medium tracking-wide text-white transition-all hover:bg-transparent hover:text-orange-600 dark:border-orange-400 dark:bg-orange-400 dark:text-gray-900 dark:hover:bg-transparent dark:hover:text-orange-400"
                                    >
                                        <Link :href="route(AUTH_ROUTES.register)">
                                            {{ t('home.hero.supplier.cta_primary') }}
                                            <ArrowRight class="ml-3 h-4 w-4 transition-transform group-hover:translate-x-1" />
                                        </Link>
                                    </Button>

                                    <Link
                                        :href="route(AUTH_ROUTES.login)"
                                        class="px-6 py-3 text-sm font-medium tracking-wide text-gray-500 underline-offset-4 transition-colors hover:text-gray-900 hover:underline dark:text-gray-400 dark:hover:text-white"
                                    >
                                        {{ t('home.hero.supplier.cta_secondary') }}
                                    </Link>
                                </div>
                            </div>

                            <!-- Right Column - Value Props Card -->
                            <div class="relative">
                                <div
                                    class="relative overflow-hidden rounded-sm border border-gray-200 bg-white p-5 shadow-2xl shadow-gray-900/5 sm:p-8 lg:p-10 dark:border-gray-700 dark:bg-gray-900/80"
                                >
                                    <div class="absolute top-0 right-0 h-16 w-16 border-b border-l border-gray-200 dark:border-gray-600"></div>

                                    <p
                                        class="mb-5 font-medium tracking-widest text-gray-400 sm:mb-7 dark:text-gray-500"
                                        style="font-size: 0.65rem; letter-spacing: 0.25em"
                                    >
                                        {{ t('home.hero.supplier.card_eyebrow') }}
                                    </p>

                                    <div class="grid grid-cols-1 gap-5 sm:grid-cols-2 sm:gap-6">
                                        <div
                                            v-for="(prop, index) in supplierValueProps"
                                            :key="prop.title"
                                            class="border-l-2 border-gray-200 pl-4 sm:pl-5 dark:border-gray-700"
                                            :class="isVisible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'"
                                            :style="`transition: all 0.8s ease-out ${0.5 + index * 0.1}s;`"
                                        >
                                            <div class="flex flex-wrap items-center gap-x-2 gap-y-1">
                                                <div
                                                    class="font-display text-base font-medium tracking-tight text-gray-900 sm:text-lg lg:text-xl dark:text-white"
                                                >
                                                    {{ prop.title }}
                                                </div>
                                                <span
                                                    v-if="prop.comingSoon"
                                                    class="inline-flex items-center rounded-full border border-orange-300 bg-orange-100 px-2 py-0.5 text-[10px] font-medium tracking-wide text-orange-700 uppercase dark:border-orange-400/40 dark:bg-orange-400/10 dark:text-orange-300"
                                                >
                                                    {{ t('common.coming_soon') }}
                                                </span>
                                            </div>
                                            <div class="mt-1 text-sm text-gray-500">{{ prop.description }}</div>
                                        </div>
                                    </div>

                                    <div class="mt-6 border-t border-gray-200 pt-5 sm:mt-8 sm:pt-6 dark:border-gray-700">
                                        <p class="text-sm leading-relaxed text-gray-500">
                                            {{ t('home.hero.supplier.tagline') }}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- ═══════════════════════════════════════ -->
                    <!-- BACK FACE: Agent                        -->
                    <!-- ═══════════════════════════════════════ -->
                    <div class="hero-flip-face hero-flip-face--back" :inert="!isFlipped || undefined">
                        <div class="grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
                            <!-- Left Column -->
                            <div>
                                <p
                                    class="mb-6 font-medium tracking-widest text-gray-400 dark:text-gray-500"
                                    style="font-size: 0.7rem; letter-spacing: 0.2em"
                                >
                                    {{ t('home.hero.agent.eyebrow') }}
                                </p>

                                <h2
                                    class="font-display text-4xl leading-[1.1] font-normal tracking-tight text-gray-900 sm:text-5xl lg:text-6xl xl:text-7xl dark:text-white"
                                >
                                    {{ t('home.hero.agent.headline_before') }}
                                    <span class="text-gray-900 italic dark:text-white">
                                        {{ t('home.hero.agent.headline_accent') }}
                                    </span>
                                </h2>

                                <p class="mt-5 max-w-lg text-lg leading-relaxed text-gray-500 lg:text-xl dark:text-gray-400">
                                    {{ t('home.hero.agent.subheadline') }}
                                </p>

                                <div class="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center">
                                    <Button
                                        as-child
                                        size="lg"
                                        class="group h-14 rounded-none border-2 border-orange-500 bg-orange-500 px-10 text-base font-medium tracking-wide text-white transition-all hover:bg-transparent hover:text-orange-600 dark:border-orange-400 dark:bg-orange-400 dark:text-gray-900 dark:hover:bg-transparent dark:hover:text-orange-400"
                                    >
                                        <Link :href="route(AUTH_ROUTES.register)">
                                            {{ t('home.hero.agent.cta_primary') }}
                                            <ArrowRight class="ml-3 h-4 w-4 transition-transform group-hover:translate-x-1" />
                                        </Link>
                                    </Button>

                                    <Link
                                        :href="route(AUTH_ROUTES.login)"
                                        class="px-6 py-3 text-sm font-medium tracking-wide text-gray-500 underline-offset-4 transition-colors hover:text-gray-900 hover:underline dark:text-gray-400 dark:hover:text-white"
                                    >
                                        {{ t('home.hero.agent.cta_secondary') }}
                                    </Link>
                                </div>
                            </div>

                            <!-- Right Column - Value Props Card -->
                            <div class="relative">
                                <div
                                    class="relative overflow-hidden rounded-sm border border-gray-200 bg-white p-5 shadow-2xl shadow-gray-900/5 sm:p-8 lg:p-10 dark:border-gray-700 dark:bg-gray-900/80"
                                >
                                    <div class="absolute top-0 right-0 h-16 w-16 border-b border-l border-gray-200 dark:border-gray-600"></div>

                                    <p
                                        class="mb-5 font-medium tracking-widest text-gray-400 sm:mb-7 dark:text-gray-500"
                                        style="font-size: 0.65rem; letter-spacing: 0.25em"
                                    >
                                        {{ t('home.hero.agent.card_eyebrow') }}
                                    </p>

                                    <div class="grid grid-cols-1 gap-5 sm:grid-cols-2 sm:gap-6">
                                        <div
                                            v-for="(prop, index) in agentValueProps"
                                            :key="prop.title"
                                            class="border-l-2 border-gray-200 pl-4 sm:pl-5 dark:border-gray-700"
                                            :class="isVisible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'"
                                            :style="`transition: all 0.8s ease-out ${0.5 + index * 0.1}s;`"
                                        >
                                            <div class="flex flex-wrap items-center gap-x-2 gap-y-1">
                                                <div
                                                    class="font-display text-base font-medium tracking-tight text-gray-900 sm:text-lg lg:text-xl dark:text-white"
                                                >
                                                    {{ prop.title }}
                                                </div>
                                                <span
                                                    v-if="prop.comingSoon"
                                                    class="inline-flex items-center rounded-full border border-orange-300 bg-orange-100 px-2 py-0.5 text-[10px] font-medium tracking-wide text-orange-700 uppercase dark:border-orange-400/40 dark:bg-orange-400/10 dark:text-orange-300"
                                                >
                                                    {{ t('common.coming_soon') }}
                                                </span>
                                            </div>
                                            <div class="mt-1 text-sm text-gray-500">{{ prop.description }}</div>
                                        </div>
                                    </div>

                                    <div class="mt-6 border-t border-gray-200 pt-5 sm:mt-8 sm:pt-6 dark:border-gray-700">
                                        <p class="text-sm leading-relaxed text-gray-500">
                                            {{ t('home.hero.agent.tagline') }}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Scroll indicator -->
        <div class="mt-8 flex justify-center pb-6 lg:mt-10">
            <a
                href="#features"
                class="group flex flex-col items-center gap-3 text-gray-400 transition-colors hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
            >
                <span class="text-xs font-medium tracking-widest" style="letter-spacing: 0.15em">DISCOVER</span>
                <div class="h-12 w-px bg-linear-to-b from-current to-transparent"></div>
            </a>
        </div>
    </section>
</template>
